import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { promoActive } from "@/lib/promo";

const SERVICE_LINKS = {
  "Interior Detailing": "/interior-detailing-edmonton",
  "Paint Correction": "/paint-correction-edmonton",
  "Ceramic Coating": "/ceramic-coating-edmonton",
  "Headlight Restoration": "/headlight-restoration-edmonton",
  "Odor Treatment": "/odor-removal-edmonton",
  "Decontamination": "/exterior-detailing-edmonton",
};

/** Homepage Grand Opening promo section — driven by pricing-config promo. */
export default function GrandOpening() {
  const [promo, setPromo] = useState(null);

  useEffect(() => {
    api.get("/pricing/config").then(({ data }) => setPromo(data.promo)).catch(() => {});
  }, []);

  if (!promoActive(promo)) return null;

  return (
    <section data-testid="promo-section" className="relative py-20 md:py-28 overflow-hidden isolate border-t border-white/10">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute right-1/4 top-0 w-[520px] h-[420px] rounded-full bg-[#1F5EFF]/14 blur-[130px]" />
        <div className="absolute left-0 bottom-0 w-[380px] h-[300px] rounded-full bg-amber-400/8 blur-[110px]" />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <div className="flex items-center gap-3 mb-5">
            <span className="w-8 h-px bg-amber-400" />
            <span className="font-mono-alt text-[12px] text-amber-300 tracking-[0.2em]" data-testid="promo-badge">
              {promo.badge}
            </span>
          </div>
          <h2 className="font-display font-light tracking-tighter leading-[0.95]">
            <span className="block text-white text-3xl md:text-5xl">GRAND OPENING</span>
            <span className="block text-[#1F5EFF] blue-text-glow text-7xl md:text-9xl mt-1" data-testid="promo-headline">
              {promo.headline}
            </span>
          </h2>
          <p className="mt-6 text-white/60 text-base md:text-lg max-w-xl leading-relaxed">
            {promo.subtext}
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5" data-testid="promo-services">
            {(promo.service_labels || []).map((label) => {
              const to = SERVICE_LINKS[label];
              const cls = "px-4 py-2 rounded-full border border-white/15 bg-white/5 text-white/80 text-sm hover:border-[#1F5EFF]/50 hover:text-white transition-colors";
              return to ? (
                <Link key={label} to={to} className={cls} data-testid={`promo-service-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {label}
                </Link>
              ) : (
                <span key={label} className={cls}>{label}</span>
              );
            })}
          </div>
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-5">
            <Link
              to="/book"
              data-testid="promo-book-btn"
              className="inline-flex items-center justify-center gap-3 px-9 py-4 rounded-full bg-[#1F5EFF] text-white font-semibold hover:bg-white hover:text-black transition-colors"
            >
              BOOK NOW <ArrowRight className="w-4 h-4" />
            </Link>
            <span className="font-mono-alt text-[11px] tracking-[0.2em] text-amber-300/80" data-testid="promo-limited">
              {promo.limited}
            </span>
          </div>
          {promo.fineprint && (
            <p className="mt-4 text-white/35 text-[11px] leading-relaxed max-w-lg" data-testid="promo-fineprint">
              {promo.fineprint}
            </p>
          )}
        </motion.div>
      </div>
    </section>
  );
}
